import { useMemo } from 'react';
import type { DocumentVersion, VersionStatus } from '@platform/shared-types';
import { activeVersion } from '@platform/shared-types';
import { Tag, WarnNote } from './ui';
import type { TagTone } from './ui';

/**
 * The version chain from mockup screen 3 — a dotted timeline, newest first,
 * with the ACTIVE marker, the scheduled marker and the self-approved warning.
 *
 * WHAT IS DERIVED AND WHAT IS NOT. `activeVersion()` comes from
 * `@platform/shared-types` — the same function the service uses — so the badge
 * on this screen can never disagree with what the API serves. Everything else
 * (who approved, how many accepted) arrives as props: this component performs
 * no I/O and reads no clock. `nowIso` is injected so the render is a pure
 * function of its props and stays testable.
 *
 * See the PALETTE RULE in ./ui.tsx before adding a class.
 */

/** The minimum a version needs to appear on the timeline. */
export type ChainVersion = Pick<
  DocumentVersion,
  'versionNo' | 'status' | 'effectiveAt' | 'changeType' | 'changeSummary'
> & Partial<Pick<
  DocumentVersion,
  'createdBy' | 'createdAt' | 'approvedBy' | 'approvedAt' | 'selfApproved'
  | 'publishedBy' | 'publishedAt'
  // The email SNAPSHOTS recorded beside each id. Optional everywhere: versions
  // acted on before these were captured have none and still render.
  | 'approvedByEmail' | 'publishedByEmail' | 'createdByEmail'
>>;

/** How each row is labelled once `activeVersion` has spoken. */
export type ChainState = 'draft' | 'in_review' | 'approved' | 'active' | 'scheduled' | 'superseded';

const STATE_LABEL: Record<ChainState, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  active: 'Published',
  scheduled: 'Published',
  superseded: 'Superseded',
};

const STATE_TAG: Record<ChainState, { tone: TagTone; label: string } | null> = {
  draft: { tone: 'draft', label: 'editing' },
  in_review: { tone: 'review', label: 'in review' },
  approved: { tone: 'approved', label: 'approved' },
  active: { tone: 'active', label: 'ACTIVE' },
  scheduled: { tone: 'scheduled', label: 'SCHEDULED' },
  superseded: null,
};

/** The colour of the dot on the timeline rail. */
const STATE_DOT: Record<ChainState, string> = {
  draft: 'bg-amber-500',
  in_review: 'bg-amber-500',
  approved: 'bg-blue-400',
  active: 'bg-emerald-500 ring-4 ring-emerald-500/20',
  scheduled: 'bg-violet-400',
  superseded: 'bg-zinc-600',
};

function stateOf(
  v: ChainVersion, activeNo: number | null, now: number,
): ChainState {
  if (v.status !== 'published') return v.status as Exclude<VersionStatus, 'published'>;
  if (activeNo !== null && v.versionNo === activeNo) return 'active';
  const at = v.effectiveAt ? Date.parse(v.effectiveAt) : NaN;
  if (!Number.isNaN(at) && at > now) return 'scheduled';
  return 'superseded';
}

/** Dates on this screen are legal dates, so they render in a fixed, unambiguous
 *  format rather than a locale-dependent one. */
function formatDay(iso?: string): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${day} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export interface VersionChainProps {
  versions: readonly ChainVersion[];
  /** Injected clock — this component never calls `Date.now()` itself. */
  nowIso: string;
  /** versionNo → number of parties that accepted that version, if known. */
  acceptanceCounts?: Record<number, number>;
  /** userId → display name, so the timeline shows people not subs. */
  actorNames?: Record<string, string>;
  selectedVersionNo?: number;
  onSelectVersion?: (versionNo: number) => void;
  className?: string;
}

export function VersionChain({
  versions, nowIso, acceptanceCounts, actorNames,
  selectedVersionNo, onSelectVersion, className = '',
}: VersionChainProps) {
  const now = Date.parse(nowIso);
  const active = useMemo(() => activeVersion(versions, nowIso), [versions, nowIso]);
  const activeNo = active ? active.versionNo : null;

  const ordered = useMemo(
    () => versions.slice().sort((a, b) => b.versionNo - a.versionNo),
    [versions],
  );

  /**
   * WHO DID IT, in the most human form available.
   *
   * Order matters: an injected display name first (a host that has resolved
   * real names), then the EMAIL SNAPSHOT recorded when the action happened,
   * and only then the raw Cognito id. A timeline reading
   * "Approved by 19a9d9fe-a021-7047…" tells a reader nothing at all, which is
   * what this fallback chain exists to avoid.
   *
   * The email is deliberately a snapshot rather than a lookup: it records the
   * address that acted, and does not rewrite itself if that person's email
   * changes later — which is the whole point of an audit trail.
   */
  const nameOf = (id?: string, email?: string) => {
    if (!id) return null;
    return actorNames?.[id] || (email && email.trim() !== '' ? email : id);
  };
  const selfApproved = ordered.filter((v) => v.selfApproved);

  if (ordered.length === 0) {
    return <p className={`text-[13px] text-stone-500 ${className}`}>No versions yet.</p>;
  }

  return (
    <div className={className}>
      <div className="relative pl-6">
        {/* the rail */}
        <span className="absolute bottom-2 left-[5px] top-2 w-px bg-stone-100" aria-hidden="true" />

        {ordered.map((v) => {
          const state = stateOf(v, activeNo, now);
          const tag = STATE_TAG[state];
          const accepted = acceptanceCounts?.[v.versionNo];
          const approver = nameOf(v.approvedBy, v.approvedByEmail);
          const publisher = nameOf(v.publishedBy, v.publishedByEmail);
          const creator = nameOf(v.createdBy);
          const selected = selectedVersionNo === v.versionNo;
          const clickable = !!onSelectVersion;

          const row = (
            <>
              <span
                className={`absolute -left-[21px] top-[18px] h-[9px] w-[9px] rounded-full border-2 border-dark-900 ${STATE_DOT[state]}`}
                aria-hidden="true"
              />
              <span className="min-w-[30px] shrink-0 text-[13px] font-bold text-stone-900">
                v{v.versionNo}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2 text-[12.5px] text-stone-400">
                  <b className="font-semibold text-stone-900">{STATE_LABEL[state]}</b>
                  {state === 'superseded' && v.effectiveAt && (
                    <span>· was effective {formatDay(v.effectiveAt)}</span>
                  )}
                  {(state === 'active' || state === 'scheduled') && v.effectiveAt && (
                    <span>· effective {formatDay(v.effectiveAt)}</span>
                  )}
                  {tag && <Tag tone={tag.tone}>{tag.label}</Tag>}
                  {v.changeType && (
                    <Tag tone={v.changeType === 'material' ? 'material' : 'minor'}>
                      {v.changeType}
                    </Tag>
                  )}
                </span>
                <span className="mt-1 block text-[11.5px] text-stone-500">
                  {approver && (
                    <>
                      Approved by {approver}
                      {v.selfApproved && (
                        <span className="text-amber-400"> (self-approved)</span>
                      )}
                    </>
                  )}
                  {approver && publisher && ' · '}
                  {publisher && <>Published by {publisher}</>}
                  {!approver && !publisher && creator && <>Created by {creator}</>}
                  {typeof accepted === 'number' && (
                    <> · {accepted} accepted</>
                  )}
                </span>
                {v.changeType === 'material' && v.changeSummary && (
                  <span className="mt-1.5 block rounded border border-dark-700 bg-white px-2.5 py-1.5 text-[11.5px] text-stone-400">
                    {v.changeSummary}
                  </span>
                )}
              </span>
            </>
          );

          return clickable ? (
            <button
              key={v.versionNo}
              type="button"
              onClick={() => onSelectVersion(v.versionNo)}
              aria-current={selected ? 'true' : undefined}
              className={`relative flex w-full min-h-[44px] items-start gap-3 rounded-lg py-3 pl-1 pr-2 text-left transition-colors hover:bg-white ${
                selected ? 'bg-white' : ''
              }`}
            >
              {row}
            </button>
          ) : (
            <div
              key={v.versionNo}
              className="relative flex min-h-[44px] items-start gap-3 py-3 pl-1 pr-2"
            >
              {row}
            </div>
          );
        })}
      </div>

      {selfApproved.length > 0 && (
        <WarnNote className="mt-4">
          <b className="font-semibold">
            {selfApproved.map((v) => `v${v.versionNo}`).join(', ')}{' '}
            {selfApproved.length === 1 ? 'was' : 'were'} self-approved.
          </b>{' '}
          Permitted when only one approver exists, but stamped on the record and in the audit
          trail. It never passes as independent sign-off.
        </WarnNote>
      )}
    </div>
  );
}
